/**
 * The three pillars of CareCo 24 / InfiniHealth business.
 * Everything on the landing page and the /services explorer is driven by this.
 *
 * Home-health services carry `hourlyRateCents` (à la carte prices) and the pillar
 * has an optional `packages` list — pre-built bundles families can pick instead
 * of assembling their own from individual services.
 */

export const PILLARS = [
    {
        key: "home-health",
        eyebrow: "For families",
        title: "Home Health & Family Portal",
        tagline:
            "Compassionate in-home care for your loved ones — booked in minutes, delivered by vetted professionals.",
        color: "emerald",
        paymentNote: "Private pay only — we don’t bill Medicare, Medicaid, or private insurance.",
        image:
            "https://images.unsplash.com/photo-1666887360362-e999f7f2c22a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxob21lJTIwaGVhbHRoJTIwY2FyZWdpdmVyfGVufDB8fHx8MTc4MzAyNjM2N3ww&ixlib=rb-4.1.0&q=85",
        services: [
            { id: "personal-care", label: "Personal care & bathing", hourlyRateCents: 2800, credentialHint: "HHA", cadence: "weekly" },
            { id: "companion", label: "Companion care", hourlyRateCents: 2400, credentialHint: "HHA", cadence: "weekly" },
            { id: "meal-prep", label: "Meal prep & nutrition", hourlyRateCents: 2200, credentialHint: "HHA", cadence: "weekly" },
            { id: "medication", label: "Medication reminders", hourlyRateCents: 2000, credentialHint: "HHA", cadence: "weekly" },
            { id: "transportation", label: "Transportation & errands", hourlyRateCents: 3000, credentialHint: "HHA", cadence: "weekly" },
            { id: "respite", label: "Respite care (family relief)", hourlyRateCents: 2600, credentialHint: "HHA", cadence: "weekly" },
            { id: "dementia", label: "Dementia & Alzheimer’s care", hourlyRateCents: 3200, credentialHint: "CNA", cadence: "weekly" },
            { id: "post-op", label: "Post-op & hospital-to-home", hourlyRateCents: 4200, credentialHint: "CNA", cadence: "weekly" },
            // Daily-cadence services — booked as hours-per-day × days-per-week (min 6 hrs/day).
            { id: "daily-day-shift", label: "Daily day-shift care (mornings/afternoons)", hourlyRateCents: 2800, credentialHint: "HHA", cadence: "daily", minHoursPerDay: 6, defaultHoursPerDay: 8, defaultDaysPerWeek: 5 },
            { id: "daily-overnight", label: "Daily overnight care (in-home, sleeping caregiver)", hourlyRateCents: 3200, credentialHint: "HHA", cadence: "daily", minHoursPerDay: 8, defaultHoursPerDay: 10, defaultDaysPerWeek: 7 },
            { id: "daily-awake-overnight", label: "Daily awake overnight (active care through the night)", hourlyRateCents: 3800, credentialHint: "CNA", cadence: "daily", minHoursPerDay: 8, defaultHoursPerDay: 10, defaultDaysPerWeek: 7 },
            { id: "daily-weekend", label: "Weekend intensive care (Sat & Sun)", hourlyRateCents: 3000, credentialHint: "HHA", cadence: "daily", minHoursPerDay: 8, defaultHoursPerDay: 10, defaultDaysPerWeek: 2 },
            { id: "daily-hospice", label: "Hospice daily comfort care", hourlyRateCents: 4200, credentialHint: "CNA", cadence: "daily", minHoursPerDay: 8, defaultHoursPerDay: 12, defaultDaysPerWeek: 7 },
            { id: "live-in", label: "Live-in / 24-7 care (min 24 hrs/day)", hourlyRateCents: 5200, credentialHint: "CNA", cadence: "daily", minHoursPerDay: 24, defaultHoursPerDay: 24, defaultDaysPerWeek: 7 },
        ],
        packages: [
            {
                id: "essential",
                name: "Essential",
                tagline: "Companion + basic personal care.",
                serviceIds: ["companion", "personal-care"],
                baseHours: 20,
                weeklyPriceCents: 49900,
                highlights: [
                    "20 hours / week of core care",
                    "Companion + Personal care & bathing",
                    "Single vetted caregiver assigned",
                ],
                recommendedCredential: "HHA",
            },
            {
                id: "comprehensive",
                name: "Comprehensive",
                tagline: "Adds meals, meds, and light errands.",
                serviceIds: ["companion", "personal-care", "meal-prep", "medication", "transportation"],
                baseHours: 30,
                weeklyPriceCents: 69900,
                highlights: [
                    "30 hours / week across 5 services",
                    "Meal prep, medication reminders, transport",
                    "Backup caregiver on standby",
                ],
                recommendedCredential: "CNA",
                featured: true,
            },
            {
                id: "premium",
                name: "Premium Care",
                tagline: "Post-op recovery + around-the-clock coverage.",
                serviceIds: ["post-op", "personal-care", "medication", "live-in"],
                baseHours: 40,
                weeklyPriceCents: 149900,
                highlights: [
                    "40 hours / week including post-op recovery care",
                    "Live-in / 24-7 rotation available",
                    "Care plan review every 30 days",
                ],
                recommendedCredential: "CNA",
            },
        ],
        primaryRole: "family",
        primaryCta: "Book care for a loved one",
    },
    {
        key: "b2b-staffing",
        eyebrow: "For hospitals & facilities",
        title: "B2B Nurse & Aide Staffing",
        tagline:
            "Fill open shifts fast. RNs, LPNs, CNAs, and home-health aides ready to work at your hospital or nursing home.",
        color: "sky",
        image:
            "https://images.unsplash.com/photo-1666887360362-e999f7f2c22a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxob21lJTIwaGVhbHRoJTIwY2FyZWdpdmVyfGVufDB8fHx8MTc4MzAyNjM2N3ww&ixlib=rb-4.1.0&q=85",
        services: [
            { id: "rn", label: "Registered Nurses (RN)", hourlyRateCents: 6500 },
            { id: "lpn", label: "Licensed Practical Nurses (LPN)", hourlyRateCents: 4800 },
            { id: "cna", label: "Certified Nursing Assistants (CNA)", hourlyRateCents: 3400 },
            { id: "hospital", label: "Hospital per-diem staffing", hourlyRateCents: 5400 },
            { id: "nursing-home", label: "Nursing home staffing", hourlyRateCents: 4200 },
            { id: "travel", label: "Travel nurse contracts", hourlyRateCents: 7200 },
        ],
        primaryRole: "agency",
        primaryCta: "Post shifts as a facility",
    },
    {
        key: "recruitment",
        eyebrow: "For businesses hiring",
        title: "Nursing Recruitment Services",
        tagline:
            "We source, screen and place nursing talent for you — or use our platform to post your own jobs and hire directly.",
        color: "orange",
        image:
            "https://images.unsplash.com/photo-1543333995-a78aea2eee50?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxob21lJTIwaGVhbHRoJTIwY2FyZWdpdmVyfGVufDB8fHx8MTc4MzAyNjM2N3ww&ixlib=rb-4.1.0&q=85",
        services: [
            { id: "managed-recruit", label: "Managed recruiting (we do it for you)" },
            { id: "post-job", label: "Post a job yourself" },
            { id: "exec-search", label: "Executive & DON search" },
            { id: "permanent", label: "Permanent placement" },
            { id: "contract-to-hire", label: "Contract-to-hire" },
            { id: "credentialing", label: "Credentialing & background checks" },
        ],
        primaryRole: "agency",
        primaryCta: "Start hiring nurses",
    },
];

export const US_STATES = [
    ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
    ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
    ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"],
    ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"],
    ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"],
    ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"],
    ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"],
    ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"],
    ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"],
    ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"],
    ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"],
    ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"],
    ["WI", "Wisconsin"], ["WY", "Wyoming"], ["DC", "District of Columbia"],
];

export function getPillar(key) {
    return PILLARS.find((p) => p.key === key);
}

export function getHomeHealthService(id) {
    const p = PILLARS.find((x) => x.key === "home-health");
    return p?.services.find((s) => s.id === id);
}

export function getHomeHealthPackage(id) {
    const p = PILLARS.find((x) => x.key === "home-health");
    return p?.packages?.find((pk) => pk.id === id);
}
